import React, { useState, useEffect, useMemo } from "react";
import Select, { components } from "react-select";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { Card, CardBody } from "@/shared/components/Card";
import FormHeader from "../../../shared/components/form/FormHeader";
import {
  FormContainer,
  FormGroupLabel,
} from "../../../shared/components/form/FormElements";
import { formStyles } from "../../../shared/constants/styles";
import InfoPopup from "../../../shared/components/InfoPopup";
import {
  colorLightBlue,
  colorLightGray,
  colorStatusActive,
} from "../../../utils/palette";
import styled from "styled-components";
import { post } from "../../../api/getUser";
import { useAuth } from "../../../contexts/AuthContext";
import SubscribePopup from "../../../shared/components/SubscribePopup";

const ROLE_OPTION_KEYS = [
  {
    value: "ALL_ADMINS",
    labelKey: "role_all_admins",
    role_group: "ALL_ADMINS",
  },
  {
    value: "ASSIGNED_MANAGERS",
    labelKey: "role_assigned_managers",
    role_group: "ASSIGNED_MANAGERS",
  },
  {
    value: "ALL_TECHNICIANS",
    labelKey: "role_all_technicians",
    role_group: "ALL_TECHNICIANS",
  },
  {
    value: "ASSIGNED_DRIVERS",
    labelKey: "role_assigned_drivers",
    role_group: "ASSIGNED_DRIVERS",
  },
];

const NotificationSettings = () => {
  const { t } = useTranslation("common");
  const { tag } = useAuth();
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);

  const users = useSelector((state) => state.users.list);
  const activeUsers = users?.filter((u) => u.user_active);

  const roleOptions = useMemo(
    () =>
      ROLE_OPTION_KEYS.map((r) => ({
        ...r,
        label: t(r.labelKey),
        type: "role",
      })),
    [t],
  );

  const groupedOptions = useMemo(
    () => [
      { label: t("roles"), options: roleOptions },
      { label: t("users"), options: activeUsers || [] },
    ],
    [roleOptions, activeUsers, t],
  );

  const groupedOptionsWithoutDrivers = useMemo(
    () => [
      {
        label: t("roles"),
        options: roleOptions.filter((r) => r.role_group !== "ASSIGNED_DRIVERS"),
      },
      {
        label: t("users"),
        options: activeUsers?.filter((u) => u.role_id !== 3) || [],
      },
    ],
    [roleOptions, activeUsers, t],
  );

  const CustomGroupHeading = (props) => (
    <StyledGroupHeading>
      <components.GroupHeading {...props} />
    </StyledGroupHeading>
  );

  const CustomOption = ({ innerProps, data }) => (
    <StyledOption {...innerProps}>
      <div>{data.label}</div>
      {data.email && <div className="text-muted small">{data.email}</div>}
    </StyledOption>
  );

  useEffect(() => {
    fetchNotificationSettings();
  }, [users]);

  const buildSelectedValues = (userIds, roleGroups) => {
    const selectedUsers = users.filter((u) => userIds?.includes(u.user_id));
    const selectedRoles = roleOptions.filter((r) =>
      roleGroups?.includes(r.role_group),
    );
    return [...selectedRoles, ...selectedUsers];
  };

  const fetchNotificationSettings = async () => {
    const response = await post("notifications", {
      querystring: "get_all_notification_users",
    });
    if (response.success) {
      const { data } = response;
      if (data?.length > 0) {
        //1 - Reminder, 2 - Inspection Schedule, 3 - Issue, 4 - WO Created, 5 - Trip Schedule, 6 - WO Closed
        const reminder = data.find((n) => n.notification_type === 1);
        const reminderValues = buildSelectedValues(
          reminder?.user_ids,
          reminder?.role_groups,
        );
        if (reminderValues.length > 0)
          setValue("reminderNotifications", reminderValues);

        const inspection = data.find((n) => n.notification_type === 2);
        const inspectionValues = buildSelectedValues(
          inspection?.user_ids,
          inspection?.role_groups,
        );
        if (inspectionValues.length > 0)
          setValue("inspectionNotifications", inspectionValues);

        const issue = data.find((n) => n.notification_type === 3);
        const issueValues = buildSelectedValues(
          issue?.user_ids,
          issue?.role_groups,
        );
        if (issueValues.length > 0) setValue("issueNotifications", issueValues);

        const woCreated = data.find((n) => n.notification_type === 4);
        const woCreatedValues = buildSelectedValues(
          woCreated?.user_ids,
          woCreated?.role_groups,
        );
        if (woCreatedValues.length > 0)
          setValue("workOrderCreatedNotifications", woCreatedValues);

        const woClose = data.find((n) => n.notification_type === 6);
        const woClosedValues = buildSelectedValues(
          woClose?.user_ids,
          woClose?.role_groups,
        );
        if (woClosedValues.length > 0)
          setValue("workOrderClosedNotifications", woClosedValues);

        const tripSchedule = data.find((n) => n.notification_type === 5);
        const tripValues = buildSelectedValues(
          tripSchedule?.user_ids,
          tripSchedule?.role_groups,
        );
        if (tripValues.length > 0)
          setValue("tripScheduleNotifications", tripValues);

        const lowStock = data.find((n) => n.notification_type === 7);
        const lowStockValues = buildSelectedValues(
          lowStock?.user_ids,
          lowStock?.role_groups,
        );
        if (lowStockValues.length > 0)
          setValue("lowStockNotifications", lowStockValues);
      }
    }
  };

  const splitSelections = (selected) => {
    const roles = selected?.filter((s) => s.type === "role") || [];
    const userItems = selected?.filter((s) => s.type !== "role") || [];
    return {
      user_ids: userItems.map((u) => u.user_id),
      role_groups: roles.map((r) => r.role_group),
    };
  };

  const onSubmit = async (data) => {
    try {
      if (tag === "free-user") {
        setShowSubscribePopup(true);
        return;
      }
      const {
        reminderNotifications,
        inspectionNotifications,
        issueNotifications,
        workOrderCreatedNotifications,
        workOrderClosedNotifications,
        tripScheduleNotifications,
        lowStockNotifications,
      } = data;

      setSaveBtnLoading(true);

      const reminder = splitSelections(reminderNotifications);
      const inspection = splitSelections(inspectionNotifications);
      const issue = splitSelections(issueNotifications);
      const woCreated = splitSelections(workOrderCreatedNotifications);
      const woClose = splitSelections(workOrderClosedNotifications);
      const tripSchedule = splitSelections(tripScheduleNotifications);
      const lowStock = splitSelections(lowStockNotifications);

      const apiData = {
        querystring: "add_edit_notification_settings",
        reminder_user_ids: reminder.user_ids,
        reminder_role_groups: reminder.role_groups,
        inspection_schedule_user_ids: inspection.user_ids,
        inspection_schedule_role_groups: inspection.role_groups,
        issue_user_ids: issue.user_ids,
        issue_role_groups: issue.role_groups,
        work_order_created_user_ids: woCreated.user_ids,
        work_order_created_role_groups: woCreated.role_groups,
        work_order_closed_user_ids: woClose.user_ids,
        work_order_closed_role_groups: woClose.role_groups,
        trip_schedule_user_ids: tripSchedule.user_ids,
        trip_schedule_role_groups: tripSchedule.role_groups,
        low_stock_user_ids: lowStock.user_ids,
        low_stock_role_groups: lowStock.role_groups,
      };

      const response = await post("notifications", apiData);
      if (response.success) {
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error("Error saving notification settings:", error);
    } finally {
      setSaveBtnLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessPopup(false);
  };

  const notificationCards = [
    {
      name: "reminderNotifications",
      icon: "fi fi-rr-alarm-clock",
      titleKey: "reminder_notifications",
      descKey: "reminder_notifications_msg",
    },
    {
      name: "inspectionNotifications",
      icon: "fi fi-rr-circle-calendar",
      titleKey: "inspection_schedule_notifications",
      descKey: "inspection_schedule_notifications_msg",
    },
    {
      name: "issueNotifications",
      icon: "fi fi-rr-engine-warning",
      titleKey: "issue_notifications",
      descKey: "issue_notifications_msg",
    },
    {
      name: "lowStockNotifications",
      icon: "fi fi-rr-box-open",
      titleKey: "low_stock_notifications",
      descKey: "low_stock_notifications_msg",
      excludeDrivers: true,
    },
    {
      name: "workOrderNotifications",
      icon: "fi fi-rr-note",
      titleKey: "work_order_notifications",
      descKey: "work_order_notifications_msg",
      isWorkOrder: true,
    },
    {
      name: "tripScheduleNotifications",
      icon: "fi fi-rr-road",
      titleKey: "trip_schedule_notifications",
      descKey: "trip_schedule_notifications_msg",
    },
  ];

  return (
    <>
      {showSuccessPopup && (
        <InfoPopup
          showPopup={showSuccessPopup}
          setShowPopup={setShowSuccessPopup}
          message={t("notification_settings_save_success_msg")}
          icon={{
            className: "fi fi-rs-check-circle",
            color: colorStatusActive,
          }}
          onClose={handleCloseSuccess}
        />
      )}

      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={t("subscribe_msg")}
        />
      )}

      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <FormHeader
          isSaveLoading={saveBtnLoading}
          title={t("notification_settings")}
          showDollarSign={tag === "free-user"}
        />

        <Container
          style={{
            height: "calc(var(--window-height, 100vh) - 150px)",
            overflowY: "auto",
          }}
        >
          <Row>
            <Col md={12}>
              {notificationCards.map(
                ({ name, icon, titleKey, descKey, isWorkOrder, excludeDrivers }) => (
                  <Card key={name} className="mb-4" style={{ height: "auto" }}>
                    <CardBody style={{ height: "auto" }}>
                      <div className="d-flex align-items-start gap-3">
                        <div className="bg-primary bg-opacity-10 px-2 py-1 rounded d-flex align-items-center justify-content-center">
                          <i
                            className={`${icon} text-primary fs-5`}
                            style={{ position: "relative", top: "3px" }}
                          ></i>
                        </div>
                        <div className="flex-grow-1">
                          <h5 className="mb-1">{t(titleKey)}</h5>
                          <p className="text-muted mb-3">{t(descKey)}</p>

                          {isWorkOrder ? (
                            <>
                              {/* When a Work Order is created */}
                              <div className="mb-4">
                                <h6
                                  className="mb-1"
                                  style={{ fontWeight: 500 }}
                                >
                                  {t("when_work_order_created")}
                                </h6>
                                <p className="text-muted small mb-2">
                                  {t("wo_created_notifications_msg")}
                                </p>
                                <FormGroupLabel className="m-0">
                                  {t("send_notifications_to")}
                                </FormGroupLabel>
                                <Controller
                                  name="workOrderCreatedNotifications"
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <Select
                                      isMulti
                                      options={groupedOptionsWithoutDrivers}
                                      value={value}
                                      onChange={(selected) =>
                                        onChange(selected || [])
                                      }
                                      placeholder={t("select_users_to_notify")}
                                      styles={formStyles}
                                      className="mt-1"
                                      closeMenuOnSelect={false}
                                      isDisabled={tag === "free-user"}
                                      components={{
                                        Option: CustomOption,
                                        GroupHeading: CustomGroupHeading,
                                      }}
                                    />
                                  )}
                                />
                              </div>

                              <hr className="my-3" />

                              {/* When a Work Order is closed */}
                              <div className="mb-3">
                                <h6
                                  className="mb-2"
                                  style={{ fontWeight: 500 }}
                                >
                                  {t("when_work_order_closed")}
                                </h6>
                                <p className="text-muted small mb-2">
                                  {t("wo_closed_notifications_msg")}
                                </p>
                                <FormGroupLabel className="m-0">
                                  {t("send_notifications_to")}
                                </FormGroupLabel>
                                <Controller
                                  name="workOrderClosedNotifications"
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <Select
                                      isMulti
                                      options={groupedOptionsWithoutDrivers}
                                      value={value}
                                      onChange={(selected) =>
                                        onChange(selected || [])
                                      }
                                      placeholder={t("select_users_to_notify")}
                                      styles={formStyles}
                                      className="mt-1"
                                      closeMenuOnSelect={false}
                                      isDisabled={tag === "free-user"}
                                      components={{
                                        Option: CustomOption,
                                        GroupHeading: CustomGroupHeading,
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="mb-3">
                              <FormGroupLabel className="m-0">
                                {t("send_notifications_to")}
                              </FormGroupLabel>

                              <Controller
                                name={name}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    isMulti
                                    options={excludeDrivers ? groupedOptionsWithoutDrivers : groupedOptions}
                                    value={value}
                                    onChange={(selected) => {
                                      onChange(selected || []);
                                    }}
                                    placeholder={t("select_users_to_notify")}
                                    styles={formStyles}
                                    className="mt-1"
                                    closeMenuOnSelect={false}
                                    isDisabled={tag === "free-user"}
                                    components={{
                                      Option: CustomOption,
                                      GroupHeading: CustomGroupHeading,
                                    }}
                                  />
                                )}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ),
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div
                className="d-flex align-items-center gap-2 text-muted small px-2 py-2"
                style={{
                  border: `1px solid ${colorLightGray}`,
                  borderRadius: "4px",
                  backgroundColor: "#f7f9fc",
                }}
              >
                <i
                  className="fi fi-rr-info"
                  style={{ position: "relative", top: "1px", flexShrink: 0 }}
                ></i>
                <span>{t("notification_email_limit_footnote")}</span>
              </div>
            </Col>
          </Row>
        </Container>
      </FormContainer>
    </>
  );
};

export default NotificationSettings;

const StyledOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  background-color: transparent;
  transition: background-color 0.2s ease;
  border-radius: 4px;

  &:hover {
    background-color: ${colorLightBlue};
  }
`;

const StyledGroupHeading = styled.div`
  .react-select__group-heading {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #6c757d;
    padding: 0.5rem 0.75rem 0.25rem;
    text-transform: uppercase;
  }
`;
